import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getTokens } from "../utils/auth";
import {
  listAdminImagesApiAdminImagesGet,
  getAdminImageApiAdminImagesImageIdGet,
  updateAdminImageApiAdminImagesImageIdPatch,
  toggleAdminImageVisibilityApiAdminImagesImageIdVisibilityPatch,
  deleteAdminImageApiAdminImagesImageIdDelete,
  uploadAdminImageApiAdminImagesUploadPost,
  bulkUpdateCategoriesApiAdminImagesBulkCategoriesPatch,
} from "../api/generated";
import type {
  ImageResponse,
  ImageListResponse,
  ImageUpdateRequest,
  ListAdminImagesApiAdminImagesGetResponse,
  GetAdminImageApiAdminImagesImageIdGetResponse,
} from "../api/generated";

// Helper to check if authentication is required
const isDevelopment = process.env.NODE_ENV === "development";
const requiresAuth = () => {
  // In development mode, don't require auth
  if (isDevelopment) return false;
  // In production, require auth token
  const { accessToken } = getTokens();
  return !accessToken;
};

// Re-export generated types for convenience
export type Image = ImageResponse;
export type { ImageListResponse, ImageUpdateRequest };

export interface ImageFilters {
  page?: number;
  page_size?: number;
  is_public?: boolean;
  category_id?: string;
  subcategory_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_order?: string;
}

// ImageUpdateData is now replaced by the generated ImageUpdateRequest type

/**
 * Hook for fetching paginated and filtered list of images.
 */
export function useImages(filters: ImageFilters = {}) {
  return useQuery<ListAdminImagesApiAdminImagesGetResponse>({
    queryKey: ["images", filters],
    queryFn: async () => {
      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      const response = await listAdminImagesApiAdminImagesGet({
        query: {
          page: filters.page,
          page_size: filters.page_size,
          is_public: filters.is_public,
          category_id: filters.category_id,
          subcategory_id: filters.subcategory_id,
          search: filters.search,
          date_from: filters.date_from,
          date_to: filters.date_to,
          sort_by: filters.sort_by,
          sort_order: filters.sort_order,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch images");
      }

      return response.data;
    },
    enabled: isDevelopment || !!getTokens().accessToken, // Run in dev mode or if authenticated
  });
}

/**
 * Hook for fetching a single image by ID.
 */
export function useImage(imageId: string | null) {
  return useQuery<GetAdminImageApiAdminImagesImageIdGetResponse>({
    queryKey: ["image", imageId],
    queryFn: async () => {
      if (!imageId) {
        throw new Error("No image ID found");
      }

      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      const response = await getAdminImageApiAdminImagesImageIdGet({
        path: { image_id: imageId },
      });

      if (!response.data) {
        throw new Error("Failed to fetch image");
      }

      return response.data;
    },
    enabled: !!imageId && (isDevelopment || !!getTokens().accessToken),
  });
}

/**
 * Hook for updating image metadata.
 */
export function useUpdateImage(options?: { showSuccessMessage?: boolean }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const showSuccessMessage = options?.showSuccessMessage !== false; // Default to true for backward compatibility

  return useMutation({
    mutationFn: async ({
      imageId,
      data,
    }: {
      imageId: string;
      data: ImageUpdateRequest;
    }) => {
      const { accessToken } = getTokens();

      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      const response = await updateAdminImageApiAdminImagesImageIdPatch({
        path: { image_id: imageId },
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to update image");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["images"] });
      queryClient.invalidateQueries({ queryKey: ["image"] });
      queryClient.invalidateQueries({ queryKey: ["galleries"] });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      if (showSuccessMessage) {
        enqueueSnackbar("Image updated successfully", { variant: "success" });
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to update image", { variant: "error" });
    },
  });
}

/**
 * Hook for toggling image visibility (public/private).
 */
export function useToggleVisibility() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (imageId: string) => {
      const { accessToken } = getTokens();

      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      const response =
        await toggleAdminImageVisibilityApiAdminImagesImageIdVisibilityPatch({
          path: { image_id: imageId },
        });

      if (!response.data) {
        throw new Error("Failed to toggle visibility");
      }

      return response.data;
    },
    onMutate: async (imageId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["images"] });

      // Snapshot previous value for rollback
      const previousImages = queryClient.getQueryData<ImageListResponse>([
        "images",
      ]);

      // Optimistically update using the TOGGLED value
      queryClient.setQueriesData<ImageListResponse>(
        { queryKey: ["images"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((img) =>
              img.id === imageId ? { ...img, is_public: !img.is_public } : img
            ),
          };
        }
      );

      return { previousImages };
    },
    onSuccess: (data, imageId) => {
      // Update with actual server state to ensure consistency
      queryClient.setQueriesData<ImageListResponse>(
        { queryKey: ["images"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((img) =>
              img.id === imageId ? { ...img, is_public: data.is_public } : img
            ),
          };
        }
      );

      enqueueSnackbar(
        `Image ${data.is_public ? "published" : "unpublished"} successfully`,
        { variant: "success" }
      );
    },
    onError: (_err, _imageId, context) => {
      // Rollback on error
      if (context?.previousImages) {
        queryClient.setQueryData(["images"], context.previousImages);
      }
      enqueueSnackbar("Failed to update visibility", { variant: "error" });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ["images"] });
    },
  });
}

/**
 * Hook for deleting an image.
 */
export function useDeleteImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (imageId: string | null | undefined) => {
      const { accessToken } = getTokens();

      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      if (!imageId) {
        throw new Error("Image ID is required");
      }

      const response = await deleteAdminImageApiAdminImagesImageIdDelete({
        path: { image_id: imageId },
      });

      if (!response.data) {
        throw new Error("Failed to delete image");
      }

      return response.data;
    },
    onMutate: async (imageId) => {
      // Optimistically remove from UI
      await queryClient.cancelQueries({ queryKey: ["images"] });

      const previousImages = queryClient.getQueryData<ImageListResponse>([
        "images",
      ]);

      queryClient.setQueriesData<ImageListResponse>(
        { queryKey: ["images"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((img) => img.id !== imageId),
            total: old.total - 1,
          };
        }
      );

      return { previousImages };
    },
    onSuccess: () => {
      enqueueSnackbar("Image deleted successfully", { variant: "success" });
    },
    onError: (_err, _imageId, context) => {
      // Rollback on error
      if (context?.previousImages) {
        queryClient.setQueryData(["images"], context.previousImages);
      }
      enqueueSnackbar("Failed to delete image", { variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["images"] });
      queryClient.invalidateQueries({ queryKey: ["image"] });
      queryClient.invalidateQueries({ queryKey: ["galleries"] });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    },
  });
}

/**
 * Hook for bulk updating image categories.
 */
export function useBulkUpdateCategories() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (params: {
      image_ids: string[];
      category_id: string | null;
      subcategory_id: string | null;
    }) => {
      if (requiresAuth()) {
        throw new Error("No access token found");
      }

      const response = await bulkUpdateCategoriesApiAdminImagesBulkCategoriesPatch({
        body: {
          image_ids: params.image_ids,
          category_id: params.category_id,
          subcategory_id: params.subcategory_id,
        },
      });

      if (!response.data) {
        throw new Error("Failed to update categories");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["images"] });
      enqueueSnackbar("Categories updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to update categories",
        { variant: "error" }
      );
    },
  });
}

export function useUploadImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (params: {
      file: File;
      public_id: string;
      folder?: string;
      title?: string;
      description?: string;
      alt_text?: string;
    }) => {
      // In development mode, allow uploads without token
      const isDevelopment = process.env.NODE_ENV === "development";
      const { accessToken } = getTokens();

      if (!isDevelopment && !accessToken) {
        console.error("[useUploadImage] No access token found");
        throw new Error("No access token found");
      }

      try {
        console.log("[useUploadImage] Starting upload...", {
          fileName: params.file.name,
          publicId: params.public_id,
          folder: params.folder,
          hasToken: !!accessToken,
          isDevelopment,
        });

        const response = await uploadAdminImageApiAdminImagesUploadPost({
          body: params,
        });

        console.log("[useUploadImage] Upload response:", response);

        // Check if response has an error
        if ("error" in response && response.error) {
          console.error("[useUploadImage] API error:", response.error);
          throw new Error(
            `Failed to upload image: ${JSON.stringify(response.error)}`
          );
        }

        if (!response.data) {
          console.error("[useUploadImage] No data in response:", response);
          throw new Error("Failed to upload image: No data returned");
        }

        console.log("[useUploadImage] Upload successful:", response.data);
        return response.data;
      } catch (error: any) {
        console.error("[useUploadImage] Upload error:", error);
        // Log more details about the error
        if (error?.response) {
          console.error("[useUploadImage] Error response:", error.response);
        }
        if (error?.message) {
          console.error("[useUploadImage] Error message:", error.message);
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["images"] });
      queryClient.invalidateQueries({ queryKey: ["image"] });
      queryClient.invalidateQueries({ queryKey: ["galleries"] });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      enqueueSnackbar("Image uploaded successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to upload image",
        { variant: "error" }
      );
    },
  });
}
